import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { LearnerReportService } from "src/app/_services/report-services/learner-report.service";
import { catchError, map, switchMap } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { of } from "rxjs";
import { LearnersReportDto, ReportDetailsDto, ReportsChart, UpdateReportsFilter } from "src/app/_models/Reports";
import { GetLearnerReportTypes, GetLearnerReportTypesSuccess, GetLearnerReportTypesError, UpdateReportSessionFilter, UpdateReportSessionFilterSuccess, UpdateReportSessionFilterError, GetReportsFilter, GetReportsFilterSuccess, GetReportsFilterError, GenerateReports, GenerateReportsSuccess, GenerateReportsError, GetReportsTableResult, GetReportsTableResultSuccess, GetReportsTableResultError, GetReportDetails, GetReportDetailsSuccess, GetReportDetailsError, UpdateTableReportByParams, UpdateTableReportByParamsSuccess, UpdateTableReportByParamsError, GetLearnerReportListLength, GetLearnerReportListLengthSuccess, GetLearnerReportListLengthError, GetLearnerReportsChart, GetLearnerReportsChartSuccess, GetLearnerReportsChartError, ResetLearnerReportTable, ResetLearnerReportTableSuccess, ResetLearnerReportTableError, ResetReportSessionFilterState, ResetLearnerReportsChart, ExportReportsTable, ExportReportsTableSuccess, ExportReportsTableError } from "./learner-report.actions";


@Injectable()
export class LearnerReportEffects {

  constructor(
    private actions$: Actions,
    private learnerReportService: LearnerReportService
  ) {}

  public getLearnerReportNamesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetLearnerReportTypes),
      switchMap((value) => 
        this.learnerReportService.getReportNames$(value.tabIndex).pipe(
          map((result: string[]) => {
            return GetLearnerReportTypesSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetLearnerReportTypesError({error: error}));
          }),
        )
      )
    )
  );

  public updateReportsFilterEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateReportSessionFilter),
      switchMap((payload) => 
        this.learnerReportService.updateReportsFilter$(
          payload.reportType, payload.filter
        ).pipe(
          map((result: boolean) => {
            return UpdateReportSessionFilterSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(UpdateReportSessionFilterError({error: error}));
          }),
        )
      )
    )
  );

  public getReportsFilterEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetReportsFilter),
      switchMap((payload) => 
        this.learnerReportService.getReportsFilter$(
          payload.reportType
        ).pipe(
          map((result: UpdateReportsFilter) => {
            return GetReportsFilterSuccess({reportsFilter: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetReportsFilterError({error: error}));
          }),
        )
      )
    )
  );

  public generateReportsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GenerateReports),
      switchMap((payload) => 
        this.learnerReportService.generateReports$(
          payload.reportType
        ).pipe(
          map((result: boolean) => {
            return GenerateReportsSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GenerateReportsError({error: error}));
          }),
        )
      )
    )
  );

  public getReportsTableResultEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetReportsTableResult),
      switchMap((payload) => 
        this.learnerReportService.getReportsTableResult$(
          payload.reportType, payload.pageNumber, payload.isInitialLoad
        ).pipe(
          map((result: LearnersReportDto[] | null) => {
            return GetReportsTableResultSuccess({result: result ?? []});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetReportsTableResultError({error: error}));
          }),
        )
      )
    )
  );

  public getReportDetailsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetReportDetails),
      switchMap((payload) => 
        this.learnerReportService.getGetReportDetails$(
          payload.reportType
        ).pipe(
          map((result: ReportDetailsDto) => {
            return GetReportDetailsSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetReportDetailsError({error: error}));
          }),
        )
      )
    )
  );

  public updateReportsTableByParamEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateTableReportByParams),
      switchMap((payload) => 
        this.learnerReportService.updateReportsTable$(
          payload.reportType, payload.params
        ).pipe(
          map((result: boolean) => {
            return UpdateTableReportByParamsSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(UpdateTableReportByParamsError({error: error}));
          }),
        )
      )
    )
  );

  public getLearnerReportsListLengthEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetLearnerReportListLength),
      switchMap((payload) => 
        this.learnerReportService.getReportsTableListLength$(
          payload.reportType
        ).pipe(
          map((result: number) => {
            return GetLearnerReportListLengthSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetLearnerReportListLengthError({error: error}));
          }),
        )
      )
    )
  );

  public getLearnerReportChartEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetLearnerReportsChart),
      switchMap((payload) => 
        this.learnerReportService.getLearnerReportsChart$(
          payload.reportType
        ).pipe(
          map((result: ReportsChart[]) => {
            return GetLearnerReportsChartSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetLearnerReportsChartError({error: error}));
          }),
        )
      )
    )
  );

  public resetLearnerReportTableEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetLearnerReportTable),
      switchMap((payload) => 
        this.learnerReportService.resetLearnerReportTable$(
          payload.reportType
        ).pipe(
          map((result: boolean) => {
            return ResetLearnerReportTableSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(ResetLearnerReportTableError({error: error}));
          }),
        )
      )
    )
  );

  public resetLearnerReportFiltersEffect$ = createEffect(() => 
    this.actions$.pipe(
      ofType(ResetLearnerReportTable),
      map(() => {
        return ResetReportSessionFilterState();
      })
    )
  );

  public resetLearnerReportChartEffect$ = createEffect(() => 
    this.actions$.pipe(
      ofType(ResetLearnerReportTable),
      map(() => {
        return ResetLearnerReportsChart();
      })
    )
  );

  
  public exportReportsTableEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportReportsTable),
      switchMap((payload) => 
        this.learnerReportService.exportReportsTable$(
          payload.reportType
        ).pipe(
          map((result: LearnersReportDto[] | null) => {
            return ExportReportsTableSuccess({result: result ?? []});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(ExportReportsTableError({error: error}));
          }),
        )
      )
    )
  );
}